import { FC } from 'react'
import Dropzone, { Accept, FileRejection } from 'react-dropzone'
import { BodySmall } from '../styles/Typography'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { CheckRing } from '../icons/CheckRing'
import { LoadingSpinner } from '../LoadingSpinner'
import { useTranslation } from 'react-i18next'

export type LoadingState = 'LOADING' | 'FAILED' | 'SUCCESS' | null

interface Props {
  acceptedFiles: Accept
  /**
   * `rejections` carries the files the drop zone turned away (wrong type, too large, too many).
   * Nothing else reports them, so a caller that drops it leaves the user with no feedback.
   */
  onDrop: (files: File[], rejections: FileRejection[]) => void
  fileUploadState?: LoadingState
  maxFiles?: number
  maxSize?: number
  errorMessage?: string
}

export const DropZone: FC<Props> = ({
  acceptedFiles,
  onDrop,
  fileUploadState = null,
  maxFiles = 1,
  maxSize,
  errorMessage,
}) => {
  const { t } = useTranslation()
  return (
    <Dropzone onDrop={onDrop} accept={acceptedFiles} maxFiles={maxFiles} maxSize={maxSize}>
      {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
        <DropZoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          {!isDragActive && !fileUploadState && <BodySmall>{t('common.file.dragFileHere')}</BodySmall>}
          {isDragActive && !isDragReject && <BodySmall>{t('common.file.dropFileHere')}</BodySmall>}
          {isDragReject && <BodySmall>{t('common.file.fileTypeNotSupported')}</BodySmall>}
          {fileUploadState === 'LOADING' && (
            <StateContainer>
              <LoadingSpinner size={48} color={theme.color.black} />
              <BodySmall>{t('common.file.uploadingFile')}</BodySmall>
            </StateContainer>
          )}
          {fileUploadState === 'FAILED' && (
            <StateContainer>
              <BodySmall>{t('common.file.uploadFailed')}</BodySmall>
              {errorMessage && <BodySmall>{errorMessage}</BodySmall>}
            </StateContainer>
          )}
          {fileUploadState === 'SUCCESS' && (
            <StateContainer>
              <CheckRing size={48} />
              <BodySmall>{t('common.file.uploadComplete')}</BodySmall>
            </StateContainer>
          )}
        </DropZoneContainer>
      )}
    </Dropzone>
  )
}

const DropZoneContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 416px;
  height: 214px;
  border: 1px dashed ${theme.color.grey3};
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  text-align: center;
  cursor: pointer;
`

const StateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.medium}px;
  padding: ${theme.spacing.xsmall}px;
`
